/**
 * AliExpress Categories Page
 *
 * Displays all computer product categories from AliExpress
 * Categories are loaded dynamically from the database via API
 */

import { Link } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { useCategories } from '../api/client';
import { CategoryIcon } from '../components/common/CategoryIcon';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export function AliExpressCategoriesPage() {
  const { data: categories, isLoading, error } = useCategories();

  if (isLoading) {
    return (
      <div className="container py-8 flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-8">
        <div className="text-center text-red-500 py-12">
          카테고리를 불러오는데 실패했습니다.
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
            <Monitor className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              컴퓨터 부품 카테고리
            </h1>
            <p className="text-gray-600">
              알리익스프레스 저렴한 컴퓨터 부품
            </p>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {(categories || []).map((category) => (
          <Link
            key={category.id}
            to={`/categories/${category.slug}`}
            className="block p-5 bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:border-orange-300 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl flex items-center justify-center text-orange-600 group-hover:from-orange-200 group-hover:to-red-200 transition-colors">
                <CategoryIcon iconName={category.icon} className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {category.description}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Info Banner */}
      <div className="mt-8 p-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          🔥 알리익스프레스 특가
        </h3>
        <p className="text-gray-600 text-sm">
          모든 제품은 알리익스프레스 제휴 링크를 통해 연결됩니다.
          저렴한 가격으로 컴퓨터 부품을 구매하세요!
        </p>
      </div>
    </div>
  );
}

export default AliExpressCategoriesPage;
